import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Typography,
  Toolbar,
  Stack,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Loader } from "../HelperFunctions/HelperFunctions";
import { useFormikContext } from "formik";

const DEFAULT_PAYMENT_LOG = {
  bankAccount: null,
  paymentMethod: null,
  transaction: null,
  exportType: null,
  reportView: "MMDDYYYY",
};

const PaymentLogCustomization = ({ open, onClose, editCustomData }) => {
  const { values, setFieldValue } = useFormikContext();
  const [loading, setLoading] = useState(false);

  const savedCustomization =
    values.customization?.["Payment Log Report"] ||
    editCustomData?.customizations?.["Payment Log Report"] ||
    editCustomData?.customizations?.["payment_log_report"] ||
    {};

  const data = {
    bankAccounts: [
      {
        id: 1,
        name: "Bank of America - ****1234",
      },
      {
        id: 2,
        name: "Chase Bank - ****5678",
      },
      {
        id: 3,
        name: "Wells Fargo - ****9012",
      },
    ],

    paymentMethods: [
      {
        id: 1,
        name: "Cash",
      },
      {
        id: 2,
        name: "Credit Card",
      },
      {
        id: 3,
        name: "Debit Card",
      },
      {
        id: 4,
        name: "ACH",
      },
    ],

    viewTransactions: [
      {
        id: "yesterday",
        name: "Yesterday",
      },
      {
        id: "today",
        name: "Today",
      },
      {
        id: "last7days",
        name: "Last 7 Days",
      },
      {
        id: "last30days",
        name: "Last 30 Days",
      },
    ],

    reportViews: [
      {
        id: "MMDDYYYY",
        name: "MM/DD/YYYY",
      },
      {
        id: "DDMMYYYY",
        name: "DD/MM/YYYY",
      },
    ],

    exportTypes: [
      {
        id: "excel",
        name: "Excel",
      },
      {
        id: "csv",
        name: "CSV",
      },
      {
        id: "pdf",
        name: "PDF",
      },
    ],
  };

  const [customizationValues, setCustomizationValues] = useState({
    ...DEFAULT_PAYMENT_LOG,
    ...savedCustomization,
  });

  useEffect(() => {
    setCustomizationValues({
      ...DEFAULT_PAYMENT_LOG,
      ...savedCustomization,
    });
  }, [
    savedCustomization.bankAccount,
    savedCustomization.paymentMethod,
    savedCustomization.transaction,
    savedCustomization.exportType,
    savedCustomization.reportView,
  ]);

  const CustomAutocomplete = ({ options, name, width = 320 }) => {
    const selectedOption =
      options.find((option) => option.id === customizationValues[name]?.id) ||
      null;

    return (
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={(event, newValue) => {
          setCustomizationValues((prev) => ({
            ...prev,
            [name]: newValue,
          }));
        }}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} placeholder="--All--" size="small" />
        )}
        sx={{ width }}
      />
    );
  };

  const handleCustomeData = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const checkedItems = values.checkedItems || {};
    setFieldValue("customization", {
      ...Object.fromEntries(
        Object.entries(values.customization || {}).filter(
          ([key]) => key.includes("Report") && checkedItems[key],
        ),
      ),
      "Payment Log Report": {
        ...DEFAULT_PAYMENT_LOG,
        ...customizationValues,
      },
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      fullScreen
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "1250px",
          width: "80%",
          height: "95%",
          overflow: "auto",
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <Toolbar sx={{ bgcolor: "#d7d7d7" }}>
        <IconButton
          size="small"
          edge="start"
          onClick={onClose}
          aria-label="close"
          style={{ backgroundColor: "#686868" }}
          disableRipple
        >
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
        <Typography
          sx={{
            ml: 2,
            flex: 1,
            color: "#1f1f1f",
          }}
          component="div"
          fontWeight={600}
          fontSize={"16px"}
        >
          Customizations
        </Typography>
        <Stack direction={"row"} gap={2}>
          <Button
            size="small"
            disableRipple
            disableElevation
            variant="contained"
            sx={{
              px: 4,
              textTransform: "none",
              backgroundColor: "#b5dcf2",
              color: "#000",
            }}
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            size="small"
            disableRipple
            disableElevation
            variant="contained"
            sx={{
              px: 4,
              textTransform: "none",
              backgroundColor: "#b5dcf2",
              color: "#000",
            }}
            type="button"
            onClick={handleCustomeData}
          >
            Save
          </Button>
        </Stack>
      </Toolbar>

      <DialogContent id="dialog-content" sx={{ position: "relative" }}>
        <Box p={2}>
          <Stack spacing={2} direction="column" alignItems="flex-start">
            {/* Bank Account */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ width: 180 }}>Bank Account</Typography>

              <CustomAutocomplete
                options={data.bankAccounts}
                name="bankAccount"
              />
            </Stack>
            {/* Payment Method */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ width: 180 }}>Payment Method</Typography>
              <CustomAutocomplete
                options={data.paymentMethods}
                name="paymentMethod"
              />
            </Stack>
            {/* View Transactions */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ width: 180 }}>View Transactions</Typography>
              <CustomAutocomplete
                options={data.viewTransactions}
                name="transaction"
              />
            </Stack>
            {/* Report View */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ width: 180 }}>Report View</Typography>
              <RadioGroup
                row
                value={customizationValues.reportView || "MMDDYYYY"}
                onChange={(event) =>
                  setCustomizationValues((prev) => ({
                    ...prev,
                    reportView: event.target.value,
                  }))
                }
              >
                {data.reportViews.map((view) => (
                  <FormControlLabel
                    key={view.id}
                    value={view.id}
                    control={<Radio size="small" />}
                    label={view.name}
                  />
                ))}
              </RadioGroup>
            </Stack>
            {/* Export Type */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ width: 180 }}>Export Type</Typography>
              <CustomAutocomplete
                options={data.exportTypes}
                name="exportType"
                width={150}
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <Loader loading={loading} />
    </Dialog>
  );
};

export default PaymentLogCustomization;
